import { segments } from '@mi-front/api-clients';
import { Station } from '@mi-front/core';
import { DateTime } from 'luxon';
import { getRangeForSegments } from '../../../stream/helpers/getRangeForSegments';
import {
  DEFAULT_TIME_RANGE_MINUTES,
  PREVIEW_MINUTES_COMPACT,
  PREVIEW_MINUTES_NORMAL,
} from '../../constants';
import { useLatestSegments } from '../../hooks/useLatestSegments';

interface UseStatusSegmentsParams {
  readonly station: Station;
  readonly status: segments.StationStatusDto;
  readonly enabled: boolean;
  readonly compact: boolean;
}

export function useStatusSegments(params: UseStatusSegmentsParams) {
  const { station, status, enabled, compact } = params;
  const isOnline = status.status === 'online';

  // Fetch range should always be the same regardless of compact mode
  // Only the preview range (display) is affected by compact mode
  const fetchRange = isOnline
    ? {
        from: DateTime.fromISO(status.latestAt)
          .minus({ minutes: DEFAULT_TIME_RANGE_MINUTES })
          .toISO()!,
        to: status.latestAt,
      }
    : {
        from: DateTime.fromISO(status.onlineCutoffAt)
          .minus({ minutes: DEFAULT_TIME_RANGE_MINUTES })
          .toISO()!,
        to: status.onlineCutoffAt,
      };

  // Fetch segments for the calculated waveform range
  const segmentsQuery = useLatestSegments({
    stationId: station.id,
    range: fetchRange,
    enabled,
  });

  const isLoading = segmentsQuery.isLoading;
  const segments = segmentsQuery.data ?? [];

  // Compact mode has less width available, so we need to show less segments in the preview
  const previewMinutes = compact ? PREVIEW_MINUTES_COMPACT : PREVIEW_MINUTES_NORMAL;

  // For online status, limit the range by previewMinutes (exceeds by one segment)
  // For offline status, use previewMinutes directly since we don't have segments
  const onlineRange = segments.length ? getRangeForSegments(segments, previewMinutes) : null;
  const offlineRange = {
    from: DateTime.fromISO(status.onlineCutoffAt).minus({ minutes: previewMinutes }).toISO()!,
    to: status.onlineCutoffAt,
  };

  return {
    isLoading,
    segments,
    previewRange: isOnline ? onlineRange! : offlineRange,
  };
}
